import { useMemo, useState } from "react";
import { Button, DataTable, SectionCard, type ColumnDef } from "@/deps/ui-library";
import {
  useAccounts,
  useRenameAccount,
  useRenameTaxCode,
  useRenameWithholdingCode,
  useTaxCodes,
  useWithholdingCodes,
} from "@/api/queries";
import type { Account, TaxCode, WithholdingCode } from "@/api/types";
import { useCan, useTaxWords } from "@/app/session";
import { RenameDialog } from "./RenameDialog";
import { TaxNamesCard } from "./TaxNamesCard";

const CODE_KEY = "code";

/**
 * The tax codes and withholding categories the pack suggested. Rates come from the pack and aren't
 * edited here, but names are the organisation's to change, and so are the accounts' (ADR-0038).
 */
export function TaxCodesTab() {
  const say = useTaxWords();
  const can = useCan();
  const { data, isPending } = useTaxCodes();
  const { data: withholding } = useWithholdingCodes();
  const { data: chart } = useAccounts();
  const renameCode = useRenameTaxCode();
  const renameAccount = useRenameAccount();
  const renameCategory = useRenameWithholdingCode();
  const [editingCode, setEditingCode] = useState<TaxCode | null>(null);
  const [editingCategory, setEditingCategory] = useState<WithholdingCode | null>(null);

  const editable = can("settings.tax.manage");
  const accountsEditable = can("accounting.coa.manage");
  const accountsOf = (code: TaxCode): Account[] =>
    (code.accountIds ?? []).flatMap((id) => chart?.find((account) => account.id === id) ?? []);
  const rows = useMemo(() => data ?? [], [data]);

  const columns: Array<ColumnDef<TaxCode>> = [
    {
      id: "code",
      header: "Code",
      cell: ({ row: { original } }) => <span className="num text-foreground">{original.code}</span>,
    },
    {
      id: "name",
      header: "Name",
      cell: ({ row: { original } }) => <span className="text-foreground">{original.name}</span>,
    },
    {
      id: "rate",
      header: "Rate",
      meta: { textAlign: "right" },
      cell: ({ row: { original } }) => (
        <span className="num text-foreground">
          {original.ratePct === "" ? "—" : `${original.ratePct}%`}
        </span>
      ),
    },
    {
      id: "recoverable",
      header: "Input tax",
      cell: ({ row: { original } }) => (
        <span className="text-foreground">
          {original.recoverable ? "Recoverable" : "Not recoverable"}
        </span>
      ),
    },
    {
      id: "accounts",
      header: "Accounts",
      cell: ({ row: { original } }) => (
        <span className="text-foreground">
          {accountsOf(original)
            .map((account) => account.name)
            .join(", ")}
        </span>
      ),
    },
    ...(editable
      ? [
          {
            id: "rename",
            header: "",
            meta: { textAlign: "right" as const },
            cell: ({ row: { original } }: { row: { original: TaxCode } }) => (
              <Button
                colorScheme="default"
                variant="ghost"
                size="xs"
                aria-label={`Rename ${original.code}`}
                onClick={() => setEditingCode(original)}
              >
                Rename
              </Button>
            ),
          },
        ]
      : []),
  ];

  const categoryColumns: Array<ColumnDef<WithholdingCode>> = [
    {
      id: "code",
      header: "Code",
      cell: ({ row: { original } }) => <span className="num text-foreground">{original.code}</span>,
    },
    {
      id: "name",
      header: "Name",
      cell: ({ row: { original } }) => <span className="text-foreground">{original.name}</span>,
    },
    {
      id: "rate",
      header: "Rate",
      meta: { textAlign: "right" },
      cell: ({ row: { original } }) => (
        <span className="num text-foreground">
          {original.ratePct === null ? "—" : `${original.ratePct}%`}
        </span>
      ),
    },
    ...(editable
      ? [
          {
            id: "rename",
            header: "",
            meta: { textAlign: "right" as const },
            cell: ({ row: { original } }: { row: { original: WithholdingCode } }) => (
              <Button
                colorScheme="default"
                variant="ghost"
                size="xs"
                aria-label={`Rename ${original.code}`}
                onClick={() => setEditingCategory(original)}
              >
                Rename
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <TaxNamesCard />
      <SectionCard title="Tax codes" flush>
        {isPending ? (
          <p className="text-muted-foreground p-4 text-sm">Loading</p>
        ) : (
          <DataTable
            aria-label="Tax codes"
            size="sm"
            enableSorting={false}
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            pagination={{ mode: "manual", show: false }}
          />
        )}
      </SectionCard>
      <SectionCard title={say("TDS categories")} flush>
        <DataTable
          aria-label={say("TDS categories")}
          size="sm"
          enableSorting={false}
          columns={categoryColumns}
          data={withholding ?? []}
          getRowId={(row) => row.code}
          loading={withholding === undefined}
          pagination={{ mode: "manual", show: false }}
        />
      </SectionCard>

      {editingCode ? (
        <RenameDialog
          title={`Rename ${editingCode.code}`}
          note="The code and rate stay the same, and so does anything already issued. An account can be shared with other tax codes, and they will show its new name too."
          fields={[
            { key: CODE_KEY, label: "Tax code name", value: editingCode.name },
            ...(accountsEditable
              ? accountsOf(editingCode).map((account) => ({
                  key: account.id,
                  label: `Account ${account.code}`,
                  value: account.name,
                }))
              : []),
          ]}
          onSave={(changed) =>
            Promise.all(
              changed.map(({ key, name }) =>
                key === CODE_KEY
                  ? renameCode.mutateAsync({ id: editingCode.id, name })
                  : renameAccount.mutateAsync({ id: key, name }),
              ),
            )
          }
          onClose={() => setEditingCode(null)}
        />
      ) : null}
      {editingCategory ? (
        <RenameDialog
          title={`Rename ${editingCategory.code}`}
          note="The code and rate stay the same."
          fields={[{ key: CODE_KEY, label: "Category name", value: editingCategory.name }]}
          onSave={(changed) =>
            Promise.all(
              changed.map(({ name }) =>
                renameCategory.mutateAsync({ code: editingCategory.code, name }),
              ),
            )
          }
          onClose={() => setEditingCategory(null)}
        />
      ) : null}
    </div>
  );
}
